// (c) chaehongee. Confidential and proprietary.

import type { ReactElement } from 'react'
import styles from '../../Canvas.module.css'
import IconButton from '../../../IconButton/IconButton'
import { Tooltip } from '../../../shared/Tooltip/Tooltip'
import { Bookmark, Check, Pen, Scan, FrameIcon, Loader2 } from 'lucide-react'

interface FrameActionButtonsProps {
    rect: { x: number; y: number; w: number; h: number }
    scale: number
    frameModeEnabled: boolean
    brushModeEnabled: boolean
    hasFrameChanges: boolean
    hasDrawingChanges: boolean
    frameDiffersFromImage: boolean
    isConfirming: boolean
    readOnly?: boolean
    isSaved?: boolean
    onSaveToggle?: () => void
    onFrameModeToggle: () => void
    onBrushModeToggle: () => void
    onFitFrame?: () => void
    onConfirm?: () => void
}

export function FrameActionButtons(props: FrameActionButtonsProps): ReactElement {
    const { rect, scale, frameModeEnabled, brushModeEnabled, hasFrameChanges, hasDrawingChanges, frameDiffersFromImage, isConfirming, readOnly = false, isSaved = false, onSaveToggle, onFrameModeToggle, onBrushModeToggle, onFitFrame, onConfirm } = props

    // Hide action buttons in read-only mode
    if (readOnly) {
        return <></>
    }

    return (
        <div
            className={styles.frameActionGroup}
            style={{
                position: 'absolute',
                top: `calc(${rect.y + rect.h}px + var(--space-sm) * ${scale})`,
                left: rect.x + rect.w,
                transform: `translate(-100%, 0) scale(${scale})`,
                transformOrigin: 'right top',
                pointerEvents: 'auto',
            }}
            data-canvas-ui="true"
        >
            {/* Save Toggle */}
            {onSaveToggle && (
                <Tooltip side="bottom" content={isSaved ? 'Unsave' : 'Save'}>
                    <IconButton
                        className={styles.frameActionButton}
                        onClick={(e) => {
                            e.stopPropagation()
                            onSaveToggle()
                        }}
                        aria-label={isSaved ? 'Unsave' : 'Save'}
                        data-active={isSaved}
                    >
                        <Bookmark
                            className={styles.icon}
                            aria-hidden="true"
                            data-canvas-ui="true"
                            fill={isSaved ? 'currentColor' : 'none'}
                        />
                    </IconButton>
                </Tooltip>
            )}

            {/* Brush Mode Toggle */}
            <Tooltip side="bottom" content="Draw">
                <IconButton
                    className={styles.frameActionButton}
                    onClick={(e) => {
                        e.stopPropagation()
                        onBrushModeToggle()
                    }}
                    aria-label="Draw"
                    disabled={readOnly}
                    data-active={brushModeEnabled}
                >
                    <Pen className={styles.icon} aria-hidden="true" data-canvas-ui="true" />
                </IconButton>
            </Tooltip>

            {/* Frame Mode Toggle */}
            <Tooltip
                side="bottom"
                content={frameModeEnabled ? 'Exit frame mode' : 'Resize or crop frame'}
            >
                <IconButton
                    className={styles.frameActionButton}
                    onClick={(e) => {
                        e.stopPropagation()
                        onFrameModeToggle()
                    }}
                    aria-label="Frame mode"
                    disabled={readOnly}
                    data-active={frameModeEnabled}
                >
                    <FrameIcon className={styles.icon} aria-hidden="true" data-canvas-ui="true" />
                </IconButton>
            </Tooltip>

            {/* Frame Mode Actions (only shown when frame mode is enabled) */}
            {frameModeEnabled && onFitFrame && (
                <Tooltip side="bottom" content="Fit frame to image">
                    <IconButton
                        className={styles.frameActionButton}
                        onClick={(e) => {
                            e.stopPropagation()
                            onFitFrame()
                        }}
                        aria-label="Fit frame to image"
                        disabled={!frameDiffersFromImage}
                    >
                        <Scan className={styles.icon} aria-hidden="true" data-canvas-ui="true" />
                    </IconButton>
                </Tooltip>
            )}

            {/* Confirm button shown in both frame mode and brush mode */}
            {(frameModeEnabled || brushModeEnabled) && onConfirm && (
                <Tooltip side="bottom" content={isConfirming ? 'Processing...' : 'Confirm'}>
                    <IconButton
                        className={styles.frameActionButton}
                        onClick={(e) => {
                            e.stopPropagation()
                            if (!isConfirming) {
                                onConfirm()
                            }
                        }}
                        aria-label={isConfirming ? "Processing..." : "Confirm frame"}
                        disabled={(!hasFrameChanges && !hasDrawingChanges) || isConfirming}
                    >
                        {isConfirming ? (
                            <Loader2 className={styles.icon + ' ' + styles.spin} aria-hidden="true" data-canvas-ui="true" />
                        ) : (
                            <Check className={styles.icon} aria-hidden="true" data-canvas-ui="true" />
                        )}
                    </IconButton>
                </Tooltip>
            )}
        </div>
    )
}

